import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
} from "react";

const STICKY_GAP = 12;

const STATIC_STYLE: CSSProperties = {
  position: "static",
  top: "auto",
  left: "auto",
  bottom: "auto",
  width: "100%",
  maxHeight: "none",
  zIndex: "auto",
};

const getStickyTop = () => {
  const header = document.querySelector(".ant-layout-header");
  if (!header) return STICKY_GAP;
  return header.getBoundingClientRect().bottom + STICKY_GAP;
};

const findScrollParents = (element: HTMLElement): (HTMLElement | Window)[] => {
  const targets: (HTMLElement | Window)[] = [window];
  let node: HTMLElement | null = element.parentElement;
  while (node) {
    const { overflowY } = getComputedStyle(node);
    if (overflowY === "auto" || overflowY === "scroll") {
      targets.push(node);
    }
    node = node.parentElement;
  }
  return targets;
};

/**
 * Keeps a side panel pinned while scrolling, using fixed positioning so it works
 * even when ancestor overflow containers break native CSS sticky.
 */
export const useStickySidePanel = (enabled = true, recalcKey?: unknown) => {
  const placeholderRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [panelStyle, setPanelStyle] = useState<CSSProperties>(STATIC_STYLE);

  const update = useCallback(() => {
    const placeholder = placeholderRef.current;
    const panel = panelRef.current;
    if (!enabled || !placeholder || !panel) {
      setPanelStyle(STATIC_STYLE);
      return;
    }

    const parent = placeholder.parentElement;
    if (!parent) return;

    if (getComputedStyle(parent).position === "static") {
      parent.style.position = "relative";
    }

    const stickyTop = getStickyTop();
    const placeholderRect = placeholder.getBoundingClientRect();
    const parentRect = parent.getBoundingClientRect();
    const panelHeight = panel.getBoundingClientRect().height;
    const minHeight = `${panelHeight}px`;

    if (placeholder.style.minHeight !== minHeight) {
      placeholder.style.minHeight = minHeight;
    }

    if (placeholderRect.top > stickyTop) {
      setPanelStyle(STATIC_STYLE);
      return;
    }

    if (parentRect.bottom >= panelHeight + stickyTop) {
      setPanelStyle({
        position: "fixed",
        top: stickyTop,
        left: placeholderRect.left,
        width: placeholderRect.width,
        maxHeight: `calc(100vh - ${stickyTop + STICKY_GAP}px)`,
        overflowY: "auto",
        zIndex: 100,
      });
      return;
    }

    setPanelStyle({
      position: "absolute",
      top: "auto",
      bottom: 0,
      left: placeholder.offsetLeft,
      width: placeholder.offsetWidth,
      maxHeight: `calc(100vh - ${stickyTop + STICKY_GAP}px)`,
      zIndex: 100,
    });
  }, [enabled]);

  useLayoutEffect(() => {
    update();
  }, [update, recalcKey]);

  useEffect(() => {
    if (!enabled) return;

    let frame = 0;
    const onChange = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(update);
    };

    const bindListeners = () => {
      const placeholder = placeholderRef.current;
      if (!placeholder) return [];

      const scrollTargets = findScrollParents(placeholder);
      scrollTargets.forEach((target) => {
        target.addEventListener("scroll", onChange, { passive: true, capture: true });
      });
      return scrollTargets;
    };

    document.addEventListener("scroll", onChange, { passive: true, capture: true });
    window.addEventListener("resize", onChange, { passive: true });
    document.addEventListener("wheel", onChange, { passive: true, capture: true });
    document.addEventListener("touchmove", onChange, { passive: true, capture: true });

    let scrollTargets = bindListeners();
    onChange();

    const resizeObserver = new ResizeObserver(onChange);
    const placeholder = placeholderRef.current;
    const panel = panelRef.current;
    const parent = placeholder?.parentElement;
    if (placeholder) resizeObserver.observe(placeholder);
    if (panel) resizeObserver.observe(panel);
    if (parent) resizeObserver.observe(parent);

    const intersectionObserver = new IntersectionObserver(onChange, {
      root: null,
      threshold: [0, 0.01, 0.25, 0.5, 0.75, 1],
    });
    if (placeholder) intersectionObserver.observe(placeholder);

    return () => {
      cancelAnimationFrame(frame);
      scrollTargets.forEach((target) => {
        target.removeEventListener("scroll", onChange, true);
      });
      document.removeEventListener("scroll", onChange, true);
      window.removeEventListener("resize", onChange);
      document.removeEventListener("wheel", onChange, true);
      document.removeEventListener("touchmove", onChange, true);
      resizeObserver.disconnect();
      intersectionObserver.disconnect();
    };
  }, [enabled, update, recalcKey]);

  return { placeholderRef, panelRef, panelStyle };
};
